import {Component, OnDestroy, OnInit}                       from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators}    from "@angular/forms";
import {NgbModal}                                           from "@ng-bootstrap/ng-bootstrap";
import {HttpErrorResponse}                                  from "@angular/common/http";
import {Router}                                             from "@angular/router";

import {AngularQuery}                                       from "../../services/query/angularquery";
import {Inactives}                                          from "./inactives";
import {ItemComponent}                                      from "../item/item.component";
import {MessageEntry}                                       from "../../services/message/message-entry";
import {Message}                                            from "../../services/message/message";
import {MessageService}                                     from "../../services/message/message.service";
import {NewsService}                                        from "../../services/news/news-service";
import {Ng4LoadingSpinnerService}                           from "ng4-loading-spinner";
import {PreviewComponent}                                   from "./preview/inactives-preview.component";
import {Player}                                             from "../../services/player/player";
import {PlayerService}                                      from "../../services/player/player.service";
import {Query}                                              from "../../services/query/query";
import {Tag}                                                from "../../services/tag/tag";
import {Team}                                               from "../../services/team/team";
import {TeamService}                                        from "../../services/team/team.service";
import {User}                                               from "../../services/user/user";

import {isNullOrUndefined}                                  from "util";
import {Subscription}                                       from "rxjs/Subscription";


@Component({
	selector: 'app-inactives',
	templateUrl: './inactives.component.html',
	styleUrls: ['./inactives.component.css'],
})


/**
 * Display an Inactives screen that allows editors to select a
 * team and a week, choose players and mark them as inactive for
 * that week.  This "writes" an article containing the information
 * and, at the discretion of the editor, publishes it.
 */
export class InactivesComponent implements OnDestroy, OnInit {

	public static NFL_DOT_COM_SOURCE_ID:number = 542;
	getInactive = Inactives.getInactive;
	isPreviewed:boolean = false;
	message:Message;
	msgSubscription: Subscription;
	noWeekChoice:string = 'Choose a week';
	playerFound:Subscription;
	playerList:Inactives;
	previewButtonDisabled:boolean = true;
	newsSubscription: Subscription;
	publishObject:Object;
	teamFound:Subscription;
	teamList:Team[] = [];
	tForm: FormGroup;
	userId: number = -1;
	weeks:String[] = [];


	/**
	 * Create the object.
	 *
	 * @param {FormBuilder}               fb              The FormBuilder.
	 * @param {NgbModal}                  modalService    The service to allow the preview modal to appear.
	 * @param {MessageService}            msgSvc          The MessageService that handles notifications.
	 * @param {NewsService}               newsService     The NewsService singleton.
	 * @param {PlayerService}             playerService   The PlayerService that gets player information.
	 * @param {Router}                    router          The Router singleton used for navigation.
	 * @param {Ng4LoadingSpinnerService}  spinnerService  Shows/hides the spinner while the user is waiting.
	 * @param {TeamService}               teamService     The TeamService that gets team information.
	 */
	constructor(

		private fb: FormBuilder,
		private modalService: NgbModal,
		protected msgSvc: MessageService,
		private newsService: NewsService,
		private playerService: PlayerService,
		private router: Router,
		private spinnerService: Ng4LoadingSpinnerService,
	    private teamService: TeamService,

	) {}


	/**
	 * Unsubscribe from all the subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.msgSubscription) {
			this.msgSubscription.unsubscribe();
		}

		if (this.newsSubscription) {
			this.newsSubscription.unsubscribe();
		}

		if (this.playerFound) {
			this.playerFound.unsubscribe();
		}

		if (this.teamFound) {
			this.teamFound.unsubscribe();
		}

	}


	/**
	 * Load the team information and initialize the Week information.
	 */
	ngOnInit() {

		// Set up the message window with an initial message.
		this.msgSvc.sendMessageEntry(new MessageEntry({

			message: 'Loading...',
			level: 1

		}));

		// Prepare the user for a wait.
		this.spinnerService.show();

		// Initialize the display of numbered weeks.
		this.weeks.push(this.noWeekChoice);
		for (let x:number = 1; x <= 18; x++) {

			this.weeks.push('Week ' + x.toString());

		}

		// Add the post-game weeks.
		this.weeks.push('Wild Card Weekend');
		this.weeks.push('Divisional Playoffs');
		this.weeks.push('Conference Championships');
		this.weeks.push('Super Bowl');


		// Set the author information from the session's User variable.
		let userString:string = sessionStorage.getItem('currentUser');
		if (null !== userString) {
			let user:User = new User(JSON.parse(userString));
			this.userId = user.id;
		}

		// Set up communications with the MessageService.
		this.msgSubscription = this.msgSvc.subject.subscribe( message => { this.message = message; } );

		// Initialize the data structure for the page.
		this.tForm = this.fb.group({

			teamSelect: [null, [Validators.required]],
			weekSelect: ['Choose a week', [Validators.required]],

		});

		// Start the retrieval of data; return the Observer and process when data arrives.
		this.teamFound = this.teamService.getTeamsBySport('nfl').subscribe(

			(response) => {

				// Pass back the array of Team objects after filtering out the invalid ones.
				for (let entry of response) {

					if ('None' === entry.conference) {
						continue;
					}

					this.teamList.push(new Team(entry));

				}

				// Notify the user.
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Ready',
					level: 1

				}));

			},
			() => {

				// Notify the user.
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Unable to load teams',
					level: 4

				}));

			},

			() => {

				// Hide the wait spinner.
				this.spinnerService.hide();

			}

		);

	}


	/**
	 * If the week SELECT or team SELECT changes, check to see if the screen needs to
	 * be updated.  If both SELECTs contain valid values, then the screen should
	 * be drawn based on the new choices.
	 */
	changed(): void {

		this.isPreviewed = false;
		if (this.noWeekChoice === this.tForm.controls['weekSelect'].value) {

			this.previewButtonDisabled = true;
			return;

		}

		if (isNullOrUndefined(this.tForm.controls['teamSelect'].value)) {

			this.previewButtonDisabled = true;
			return;

		}

		this.previewButtonDisabled = false;
		this.redraw(this.tForm.controls['teamSelect'].value);

	}


	/**
	 * Create the preview and display it for approval.
	 */
	preview(): void {

		let values:Object = this.tForm.value;
		let team:Team;

		// Get the Team object.
		for (let entry of this.teamList) {

			if (entry.id == values['teamSelect']) {

				team = entry;
				break;

			}

		}

		let articleBody:string = 'The ' + team.title + ' have declared ';
		let foundPlayer:boolean = false;

		// Get the selected Player objects.
		for (let row of this.playerList.getRows()) {

			for (let player of row) {

				if (values['player-' + player.id]) {

					articleBody = articleBody.concat(player.position_title, ' ', player.firstname, ' ', player.surname, ', ');
					foundPlayer = true;

				}

			}

		}

		// If they didn't select any players, then exit now.
		if (! foundPlayer) {

			this.msgSvc.sendMessageEntry(new MessageEntry({

				message: "You didn't select any players, so no preview will be generated",
				level: 4

			}));

			return;

		}


		// Turn on the spinner so they know to wait.
		this.spinnerService.show();

		// Finish formatting the article text.
		articleBody = articleBody.substring(0, articleBody.length - 2);
		let pointer:number = articleBody.lastIndexOf(',');

		if (-1 !== pointer) {

			articleBody = articleBody.substring(0, pointer) + ' and' + articleBody.substr(pointer + 1);

		}

		let inactiveWeek:string = values['weekSelect'] === 'Super Bowl' ? 'the Super Bowl' : values['weekSelect'];
		articleBody = articleBody.concat(' inactive for ', inactiveWeek, '.');

		// Create the query that will give us a URL.
		let query: AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_NEWS_PARSER);
		query.useParams(false);
		query.useCacheBuster(false);

		// Create the object that contains the preview data.
		let post: Object = {};
		post['article'] = articleBody;
		post['code'] = 'NFL';
		post['title'] = team.shorttitle + ' | ' + values['weekSelect'] + ' ' + team.title + ' inactives';
		post['team'] = team.id;

		// Clear any existing preview subscriptions so we always start fresh.
		if (this.newsSubscription) {
			this.newsSubscription.unsubscribe();
		}

		this.newsSubscription = this.newsService.postParseRequest(query.getUrl(), JSON.stringify(post)).subscribe(

			(response) => {

				// Save the players and tags arrays plus the team object returned by the API.
				const modalRef = this.modalService.open(PreviewComponent);
				modalRef.componentInstance.title = post['title'];
				modalRef.componentInstance.text = post['article'];

				modalRef.result.then((result) => {

					this.publishObject = post;
					this.publishObject['team'] = [];
					this.publishObject['team'].push(response['team']);
					this.publishObject['user_id'] = this.userId;
					this.publishObject['date'] = ItemComponent.formatDateToMysql(new Date());
					this.publishObject['hot'] = 0;
					this.publishObject['visible'] = 1;
					this.publishObject['source_id'] = InactivesComponent.NFL_DOT_COM_SOURCE_ID; // NOTE: Hard-coded value.
					this.publishObject['original_url'] = 'https://www.nfl.com/'; // NOTE: Hard-coded value.
					this.publishObject['body'] = articleBody;

					// Turn the players and tags arrays into IDs only.
					this.publishObject['tags'] = [];
					this.publishObject['players'] = [];
					let tags:Tag[] = response['tags'];
					let players:Player[] = response['players'];

					for (let p of players) {

						this.publishObject['players'].push(p.id);

					}

					for (let t of tags) {

						this.publishObject['tags'].push(t.id);

					}

					// Mark the item as ready to publish.
					this.isPreviewed = true;
					this.publish();

				}).catch( (result) => {

					// Turn off the spinner.
					this.spinnerService.hide();

					this.msgSvc.sendMessageEntry(new MessageEntry({

						message: "Editor canceled the article publish",
						level: 4

					}));

				});

			},

			(error) => {

				let msg: string = 'Unable to preview article';

				if (error instanceof HttpErrorResponse) {

					if (404 === error.status) {

						msg = Query.getQueryErrorMessage(error);

					} else {

						let body:any = error || '';

						if (! isNullOrUndefined(body.message)) {

							msg = 'Unable to preview: ' + body.message;

						}

					}

				}

				// Turn off the spinner.
				this.spinnerService.hide();
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: msg,
					level: 4

				}));

			},

			() => {

				// Turn off the spinner.
				this.spinnerService.hide();

			}
		);

	}


	/**
	 * Publish the publishObject as a new story.
	 */
	publish():void {

		// Inform the user
		this.msgSvc.sendMessageEntry(new MessageEntry({

			message: 'Publishing ...',
			level: 1

		}));

		// Create the POST query for a new article.
		let query: AngularQuery = new AngularQuery();
		query.useParams(false);
		query.useCacheBuster(false);
		query.setEndpoint(AngularQuery.ENDPOINT_NEWS, 'new');

		if (this.newsSubscription) {
			this.newsSubscription.unsubscribe();
		}

		// Create the object to generate the JSON for the POST.
		this.newsSubscription = this.newsService.postNewsItem(query.getUrl(), JSON.stringify(this.publishObject)).subscribe(

			(response) => {

				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Article published successfully',
					level: 2,
					linkText: '(View)',
					link: '/articles/item/' + response

				}));

				// Keep it from being published again.
				this.isPreviewed = false;

			},

			(error) => {

				// Turn off the spinner.
				this.spinnerService.hide();

				// Figure out what happened.
				let msg: string = 'Article did not publish';

				if (error instanceof HttpErrorResponse) {

					if (404 === error.status) {

						msg = Query.getQueryErrorMessage(error);

					} else {

						let body:any = error || '';

						if (! isNullOrUndefined(body.message)) {

							msg = 'Unable to publish article: ' + body.message;

						}

					}

				}

				// Notify the user.
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: msg,
					level: 4

				}));

			},

			() => {

				// Turn off the spinner.
				this.spinnerService.hide();

			}

		);

	}


	/**
	 * Draw the list of players for the selected team.
	 *
	 * @param id  The Team ID number to retrieve players for.
	 */
	redraw(id:number):void {

		// Notify the user.
		this.msgSvc.sendMessageEntry(new MessageEntry({

			message: 'Loading players ...',
			level: 1

		}));

		// Prepare the user for a wait.
		this.spinnerService.show();

		if (this.playerFound) {

			this.playerFound.unsubscribe();

		}

		// Get the list of players.
		this.playerFound = this.playerService.getNflPlayers(id).subscribe(

			(response) => {

				// Format the response into the specified number of columns and set the list.
				let players:Inactives = new Inactives(response, 5);
				this.playerList = players;

				// Dynamically add the controls to the page.
				for (let row of players.getRows()) {

					for (let player of row) {

						this.tForm.addControl('player-' + player.id, new FormControl(false, []));

					}

				}

				// Notify the user.
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Ready',
					level: 1

				}));

			},
			() => {

				// Notify the user.
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Unable to load players',
					level: 4

				}));

			},

			() => {

				// Prepare the user for a wait.
				this.spinnerService.hide();

			}


		);

	}


	/**
	 * Switch back to the story list.
	 *
	 */
	viewStoryList(): void {

		this.router.navigate(['/articles/list']);

	}

}



// WEBPACK FOOTER //
// ./src/app/articles/inactives/inactives.component.ts